import { useTranslation } from "gatsby-plugin-react-i18next";
import { useMemo, type FC } from "react";
import ProductCard from "../../components/ProductCard";
import type { ProductCardProps } from "../../components/ProductCard/ProductCard";
import ProductCardSlider from "../../components/ProductCardSlider/ProductCardSlider";
import GridItem from "../../components/general/GridItem";
import ImageContainer from "../../components/general/ImageContainers";
import Link from "../../components/general/Link";
import { useAppState } from "../../context/app/context";
import { useIsInPreviewEditor } from "../../hooks/use-is-in-preview-editor";
import { mapStoryblokVariantToProductCard } from "../../utils/product-card";
import { notUndefined } from "../../utils/type-guards/general";

const ProductsSlideModule: FC<Queries.ProductsSlideModuleFragment> = ({
	title,
	label,
	storyblok_product_variant_pages,
	products,
}) => {
	const { t } = useTranslation();
	const { siteConfig, userCountry } = useAppState();

	const isInPreviewEditor = useIsInPreviewEditor();

	const productCards = useMemo(() => {
		return isInPreviewEditor
			? products?.map(() => placeholderProduct)
			: storyblok_product_variant_pages
					?.map<
						ProductCardProps | undefined
					>((variantPage) => mapStoryblokVariantToProductCard({ variantPage, currency: siteConfig.currency, country: userCountry }))
					.filter(notUndefined);
	}, [
		isInPreviewEditor,
		products,
		siteConfig.currency,
		storyblok_product_variant_pages,
		userCountry,
	]);

	return productCards && productCards.length > 0 ? (
		<GridItem fullWidth>
			<ProductCardSlider
				label={label}
				title={title}
				ariaLabelNext={t("next")}
				ariaLabelPrev={t("previous")}
			>
				{productCards.map((productCard, index) => {
					return (
						<ProductCard
							{...productCard}
							key={`${productCard.name}-${index}`}
						/>
					);
				})}
			</ProductCardSlider>
		</GridItem>
	) : null;
};

const placeholderProduct: ProductCardProps = {
	name: "[PRODUCT NAME]",
	rawPrice: {
		centAmount: 9995,
		currencyCode: "USD",
		fractionDigits: 2,
		type: "centPrecision",
	},
	image: (
		<ImageContainer
			alt="Placeholder product image"
			ratio="portrait"
			objectFit="cover"
			imgSrc="https://placehold.jp/30/666666/ffffff/500x500.png?text=Product+image"
		/>
	),
	fabric: "[FABRIC]",
	color: "[COLOR]",
	isNew: true,
	newLabel: "[LABEL]",
	children: <></>,
	linkComponent: (children) => <Link href="">{children}</Link>,
};
export default ProductsSlideModule;
